import React, { useEffect, useState } from "react";
import { baseURL } from "../../config/api";
import axios from 'axios';
import { formatUSPhone, getUSPhoneDigits } from "../../utils/phoneFormatter";

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    question: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (formData.email.trim() !== "") {
      timer = setTimeout(() => {
        const valid = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(formData.email.trim());
        setIsEmailValid(valid);
      }, 800);
    } else {
      setIsEmailValid(true);
    }

    return () => clearTimeout(timer);
  }, [formData.email]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (formData.phone.trim() !== "") {
      timer = setTimeout(() => {
        const valid = /^\d{10}$/.test(formData.phone);
        setIsPhoneValid(valid);
      }, 500);
    } else {
      setIsPhoneValid(true);
    }

    return () => clearTimeout(timer);
  }, [formData.phone]);

  const isFormValid =
    formData.name.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.phone.trim() !== "" &&
    formData.question.trim() !== "" &&
    isEmailValid &&
    isPhoneValid &&
    !loading;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isEmailValid) return;

    setLoading(true);

    try {
      await axios.post(
        `${baseURL}customerEnquiries`,
        JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          question: formData.question,
          created: new Date(),
        }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );

      resetForm();
      setSubmitted(true);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      question: "",
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <section className="bg-gradient-to-r from-gray-900 to-black text-white py-20 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">
          Contact <span className="text-primary">Express Tire Works</span>
        </h1>

        <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
          Have questions about tires, wheels, or bookings?
          Our team is ready to help — quickly and hassle-free.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900">
            We're Here to Help
          </h2>

          <p className="text-gray-600 leading-relaxed">
            Whether you need help choosing the right tires, booking an appointment,
            or have questions about our services — our specialists will respond
            promptly.
          </p>

          <div className="space-y-4 mt-6">

            <div className="flex items-start gap-4">
              <div className="text-primary text-xl">⚡</div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Fast Response
                </h3>
                <p className="text-gray-600 text-sm">
                  Our team reviews all enquiries and responds as soon as possible.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="text-primary text-xl">🛠️</div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Expert Assistance
                </h3>
                <p className="text-gray-600 text-sm">
                  Get advice from experienced tire specialists.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="text-primary text-xl">⭐</div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Trusted Service
                </h3>
                <p className="text-gray-600 text-sm">
                  Thousands of satisfied customers.
                </p>
              </div>
            </div>

          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">

          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Send Us a Message
          </h2>

          {submitted && (
            <div className="mb-6 p-4 rounded-lg bg-green-50 text-green-700 font-medium">
              ✅ Thank you! We'll be in touch soon.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-primary focus:outline-none"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>

              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-primary focus:outline-none"
                placeholder="you@example.com"
              />

              {!isEmailValid && (
                <p className="text-xs text-red-500 mt-2">
                  Please enter a valid email address
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>

              <input
                type="tel"
                name="phone"
                value={formatUSPhone(formData.phone)}
                onChange={(e) => {
                  const digits = getUSPhoneDigits(e.target.value);
                  setFormData((prev) => ({ ...prev, phone: digits }));
                }}
                required
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-primary focus:outline-none"
                placeholder="(555) 123-4567"
              />

              {!isPhoneValid && (
                <p className="text-xs text-red-500 mt-2">
                  Please enter a valid 10-digit phone number
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                name="question"
                value={formData.question}
                onChange={handleChange}
                rows={4}
                required
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-primary focus:outline-none"
                placeholder="How can we assist you?"
              />
            </div>

            <button
              type="submit"
              disabled={!isFormValid}
              className="w-full bg-primary text-black font-semibold py-3 rounded-xl hover:bg-yellow-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>

          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;